import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Play, Loader2, TrendingUp, TrendingDown, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { useAccounts } from "@/context/AccountContext";
import BacktestWizard from "@/components/BacktestWizard";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const StatusPill = ({ status }) => {
  const map = {
    completed: { c: "#10b981", label: "COMPLETED" },
    running:   { c: "#3b82f6", label: "RUNNING" },
    failed:    { c: "#ef4444", label: "FAILED" },
    queued:    { c: "#f59e0b", label: "QUEUED" },
  };
  const s = map[status] || { c: "#6b7280", label: (status || "?").toUpperCase() };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest border"
      style={{ color: s.c, borderColor: `${s.c}55`, background: `${s.c}18` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.c }} /> {s.label}
    </span>
  );
};

export default function BacktestPage() {
  const { activeAccountId } = useAccounts();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  const load = async () => {
    if (!activeAccountId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/backtest?account_id=${activeAccountId}`);
      setHistory(r.data?.backtests || []);
    } catch {
      toast.error("Failed to load backtest history");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeAccountId]);

  return (
    <div className="space-y-6" data-testid="backtest-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="vx-label">Historical Simulator</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Backtest</h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Replay the full detection + risk pipeline over any historical window.
            Optionally apply account-specific learned weights to see how your evolving edge scores would have played.
          </p>
          <div className="mt-3 rounded border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3 max-w-2xl" data-testid="backtest-tester-note">
            <div className="text-[11px] font-mono text-[#f59e0b] font-bold flex items-center gap-2">
              <span>ℹ</span> Why not MT5's built-in Strategy Tester?
            </div>
            <div className="text-[11px] text-[#9ca3af] mt-1">
              Our AI engine runs on the backend. MT5's Strategy Tester isolates EAs and blocks the WebRequest calls we need to fetch signals — so it can't drive this system. Use this page instead: same detection logic, historical data, full P&amp;L simulation.
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          data-testid="new-backtest-btn"
          className="h-10 px-5 rounded bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          <Rocket className="w-4 h-4" /> New Backtest
        </button>
      </div>

      <div className="vx-card p-4" data-testid="backtest-history-card">
        <div className="flex items-center justify-between mb-3">
          <div className="vx-label">Recent runs</div>
          <span className="text-[10px] font-mono text-[#6b7280]">{history.length} total</span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-[#9ca3af] text-sm font-mono py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-10" data-testid="backtest-empty">
            <FlaskConical className="w-8 h-8 text-[#6b7280] mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-sm text-[#9ca3af]">No backtests yet. Run your first one to build a track record.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#1f2937]">
                  <th className="py-2 px-2 vx-label">Started</th>
                  <th className="py-2 px-2 vx-label">Symbol</th>
                  <th className="py-2 px-2 vx-label">TF</th>
                  <th className="py-2 px-2 vx-label">Window</th>
                  <th className="py-2 px-2 vx-label">Status</th>
                  <th className="py-2 px-2 vx-label text-right">Trades</th>
                  <th className="py-2 px-2 vx-label text-right">Win%</th>
                  <th className="py-2 px-2 vx-label text-right">P&amp;L%</th>
                  <th className="py-2 px-2 vx-label text-right">Max DD</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const r = h.results || {};
                  const pnl = Number(r.total_pnl_pct || 0);
                  const positive = pnl >= 0;
                  return (
                    <tr key={h.id} className="border-b border-[#1f2937] hover:bg-[#1f2937]/40" data-testid={`backtest-row-${h.id}`}>
                      <td className="py-2 px-2 text-xs font-mono text-[#9ca3af]">{fmtDate(h.started_at)}</td>
                      <td className="py-2 px-2 font-mono font-bold">{h.symbol}</td>
                      <td className="py-2 px-2 text-xs font-mono">{h.timeframe}</td>
                      <td className="py-2 px-2 text-[10px] font-mono text-[#6b7280]">
                        {h.start_date?.slice(0, 10)} → {h.end_date?.slice(0, 10)}
                      </td>
                      <td className="py-2 px-2"><StatusPill status={h.status} /></td>
                      <td className="py-2 px-2 text-right font-mono">{r.total_trades ?? "—"}</td>
                      <td className="py-2 px-2 text-right font-mono">{r.win_rate_pct !== undefined ? `${r.win_rate_pct}%` : "—"}</td>
                      <td className="py-2 px-2 text-right font-mono font-bold" style={{ color: positive ? "#10b981" : "#ef4444" }}>
                        {h.status === "completed" ? (
                          <span className="inline-flex items-center gap-1 justify-end">
                            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {positive ? "+" : ""}{pnl}%
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-[#f59e0b]">{r.max_drawdown_pct !== undefined ? `${r.max_drawdown_pct}%` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BacktestWizard
        open={showWizard}
        onClose={() => { setShowWizard(false); load(); }}
      />
    </div>
  );
}
